import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ArrowPathIcon, ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../../store/billingStore';
import { useBilling } from '../../hooks/useBilling';
import type { InvoiceTheme } from '../../types/billing.types';

interface ThemePreviewPanelProps {
  theme: Partial<InvoiceTheme>;
}

const ThemePreviewPanel: React.FC<ThemePreviewPanelProps> = ({ theme }) => {
  const { fetchBills, previewWithTheme, downloadInvoicePdf } = useBilling();
  const bills = useBillingStore((s) => s.bills);
  const [selectedBillId, setSelectedBillId] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load bills for the sample picker
  useEffect(() => {
    if (bills.length === 0) {
      fetchBills().catch(() => {});
    }
  }, []);

  // Auto-select the first bill if none selected
  useEffect(() => {
    if (!selectedBillId && bills.length > 0) {
      setSelectedBillId(bills[0].bill_id);
    }
  }, [bills, selectedBillId]);

  const loadPreview = useCallback(async () => {
    if (!selectedBillId) return;
    setLoading(true);
    setError(null);
    try {
      const html = await previewWithTheme(selectedBillId, theme as Record<string, unknown>);
      setPreviewHtml(html);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [selectedBillId, theme, previewWithTheme]);

  // Debounced preview reload on theme changes
  useEffect(() => {
    if (!selectedBillId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadPreview();
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selectedBillId, theme]);

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownloadPdf = () => {
    if (!selectedBillId) return;
    downloadInvoicePdf(selectedBillId, theme.theme_id || undefined).catch(() => {});
  };

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Sample:</label>
          <select
            value={selectedBillId}
            onChange={(e) => setSelectedBillId(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {bills.length === 0 && <option value="">No invoices available</option>}
            {bills.map((b) => (
              <option key={b.bill_id} value={b.bill_id}>
                {b.bill_number || b.bill_id.slice(0, 8)} — {b.status}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={loadPreview}
            disabled={loading || !selectedBillId}
            className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Refresh preview"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handlePrint}
            disabled={!previewHtml}
            className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Print"
          >
            <PrinterIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={!selectedBillId}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
            title="Download PDF"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            <span>PDF</span>
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900">
        {error && (
          <div className="m-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
            <p className="text-xs text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {loading && !previewHtml && (
          <div className="flex items-center justify-center py-20">
            <div className="h-5 w-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading preview...</span>
          </div>
        )}

        {!selectedBillId && !loading && (
          <div className="flex items-center justify-center py-20">
            <p className="text-sm text-gray-400 dark:text-gray-500">
              No invoices available for preview. Create an invoice first.
            </p>
          </div>
        )}

        {previewHtml && (
          <div className="p-4">
            <div className="bg-white rounded shadow-lg overflow-hidden mx-auto" style={{ maxWidth: '850px' }}>
              <iframe
                ref={iframeRef}
                srcDoc={previewHtml}
                title="Theme Preview"
                className="w-full border-0"
                style={{ minHeight: '1000px', height: '100%' }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemePreviewPanel;
